import type { CallInfo } from '@compass/client/types/call';
import { Check, Link2, Phone } from 'lucide-react';
import React from 'react';

import { AudioPlayer } from '@/components/AudioPlayer/AudioPlayer';
import { AudioErrorBoundary } from '@/components/ErrorBoundary/AudioErrorBoundary';
import { FormattedDateTime } from '@/components/FormattedDateTime';
import { Button } from '@/components/ui/button';
import { useCallAudioAssets } from '@/hooks/useCallAudioAssets';
import { formatCallDuration } from '@/utils/date';

interface CallHistoryItemProps {
  call: CallInfo;
  isExpanded: boolean;
  isLinking: boolean;
  isLinked: boolean;
  onToggle: () => void;
  onLink: (call: CallInfo) => void;
}

export const CallHistoryItem: React.FC<CallHistoryItemProps> = ({
  call,
  isExpanded,
  isLinking,
  isLinked,
  onToggle,
  onLink,
}) => {
  const phoneNumber = call?.input_data?.reporter_phone;
  // Genesys calls carry conversation_id + queue_name; older CXOne calls carry skill_name.
  // TODO(AQ-712): drop the CXOne branch once no program has active CXOne calls.
  const isGenesys = !!call?.input_data?.conversation_id;
  const queueLabel = isGenesys ? 'Queue' : 'Skill';
  const queueName = isGenesys ? call?.input_data?.queue_name : call?.input_data?.skill_name;
  const hasAssets = !!call.assets?.length;

  // Use started_at if available, fall back to input_data.call_start_time, then created_at
  const callStartTime = call.started_at || call.input_data?.call_start_time || call.created_at;
  // Duration: call_end_time - call_start_time from input_data
  const duration = formatCallDuration(
    call.input_data?.call_start_time,
    call.input_data?.call_end_time,
  );

  // Try input_data.reporter_name first, fall back to extracted_data fields
  const contactName = call.extracted_data?.input?.data?.patient_full_name || '—';
  const hasCaseRecord = !!call.caserecord_id;

  // Fetch full call details and extract audio assets for playback
  const { audioAssets, fullCall } = useCallAudioAssets(call, isExpanded && hasAssets);

  return (
    <div
      className={`rounded-lg border bg-card transition-colors ${isExpanded ? 'ring-1 ring-primary' : hasAssets ? 'hover:bg-muted/50' : ''}`}
    >
      {/* Card header — only clickable if assets exist */}
      <div
        role={hasAssets ? 'button' : undefined}
        tabIndex={hasAssets ? 0 : undefined}
        className={`w-full px-3 py-3 text-left ${hasAssets ? 'cursor-pointer' : ''}`}
        onClick={hasAssets ? onToggle : undefined}
        onKeyDown={
          hasAssets
            ? e => {
                if (e.key === 'Enter' || e.key === ' ') onToggle();
              }
            : undefined
        }
      >
        {/* Row 1: Reporter phone + Link button */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Phone className="h-4 w-4 shrink-0 text-primary" />
            <span className="text-sm font-medium text-primary truncate">{phoneNumber || '—'}</span>
          </div>
          {isLinked ? (
            <Button variant="outline" size="xs" className="shrink-0 gap-1" disabled>
              <Check className="h-3 w-3" />
              Linked
            </Button>
          ) : (
            <Button
              variant="default"
              size="xs"
              className="shrink-0 gap-1"
              disabled={!hasCaseRecord || isLinking}
              onClick={e => {
                e.stopPropagation();
                onLink(call);
              }}
            >
              <Link2 className="h-3 w-3" />
              {isLinking ? 'Linking...' : 'Link'}
            </Button>
          )}
        </div>

        {/* Row 1.5: Queue (Skill for CXOne calls) */}
        <div className="mt-1 px-1 flex items-center">
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">{queueLabel}</span>
            <span className="text-sm font-medium text-foreground truncate">{queueName || '—'}</span>
          </div>
        </div>

        {/* Row 2: Call time (left) + Duration (right) */}
        <div className="mt-2 px-1 flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">Time</span>
            <FormattedDateTime
              value={callStartTime}
              className="text-sm font-medium text-foreground"
            />
          </div>
          <div className="flex flex-col items-end">
            <span className="text-xs text-muted-foreground">Duration</span>
            <span className="text-sm font-medium text-foreground">{duration}</span>
          </div>
        </div>

        {/* Row 3: Reporter name + phone OR Recording in Progress indicator */}
        {hasAssets ? (
          <div className="mt-1 px-1 flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">Name</span>
              <span className="text-sm font-medium text-foreground">{contactName}</span>
            </div>
            <div className="flex flex-col items-end">
              <span className="text-xs text-muted-foreground">DOB</span>
              <span className="text-sm font-medium text-foreground">
                {call.extracted_data?.input?.data?.patient_dob || '—'}
              </span>
            </div>
          </div>
        ) : (
          <div className="mt-1 pl-6 pr-1 flex items-center justify-end">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-yellow-500">
              <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
              Recording in Progress
            </span>
          </div>
        )}
      </div>

      {/* Expanded View - Audio player (only when assets exist) */}
      {isExpanded && hasAssets && audioAssets.length > 0 && call.id && call.caserecord_id && (
        <div className="border-t px-3 pb-3 pt-3">
          <div className="rounded-md border overflow-hidden">
            <AudioErrorBoundary>
              <AudioPlayer
                caseRecordId={call.caserecord_id}
                callId={call.id}
                callDetails={{ sid: fullCall?.sid }}
                assets={audioAssets}
              />
            </AudioErrorBoundary>
          </div>
        </div>
      )}
    </div>
  );
};
